import { useState } from "react";
import { Menu, X } from "lucide-react";
import { Logo } from "./Logo";
import { NAV_LINKS, NAV_IDS } from "@/data/content";
import { scrollToId } from "@/lib/scroll";
import { useScrollSpy } from "./useScrollSpy";
import { useTryIt } from "./useTryIt";

const NavLink = ({ link, prefix, active, onNavigate, className }) => (
  <a
    href={`#${link.id}`}
    onClick={(e) => {
      e.preventDefault();
      onNavigate?.();
      requestAnimationFrame(() => scrollToId(link.id, { updateHash: true }));
    }}
    className={`${className}${active ? " is-active" : ""}`}
    aria-current={active ? "true" : undefined}
    data-testid={`${prefix}-${link.key}`}
  >
    {link.label}
  </a>
);

export const Header = () => {
  const [open, setOpen] = useState(false);
  const { tryIt, busy } = useTryIt();
  const active = useScrollSpy(NAV_IDS);
  return (
    <header className="fixed inset-x-0 top-0 z-50 border-b border-[#bdbdb4]/50 bg-[#f5f5ee]/85 backdrop-blur-md" data-testid="site-header">
      <div className="mx-auto flex h-16 max-w-[1360px] items-center justify-between gap-4 px-5 sm:px-8">
        <a href="#top" onClick={(e) => { e.preventDefault(); setOpen(false); window.scrollTo({ top: 0, behavior: "smooth" }); }} aria-label="Condoor home" data-testid="header-logo-link">
          <Logo className="h-7 sm:h-8" testId="header-logo" />
        </a>
        <nav className="hidden items-center gap-6 lg:flex xl:gap-8" aria-label="Primary" data-testid="primary-nav">
          {NAV_LINKS.map((l) => <NavLink key={l.key} link={l} active={active === l.id} prefix="nav-link" className="cd-navlink text-sm text-[#292929]" />)}
        </nav>
        <div className="flex items-center gap-2">
          <button type="button" onClick={() => { setOpen(false); tryIt(); }} disabled={busy} className="cd-btn-primary h-10 px-5 text-sm" data-testid="header-try-it-button">
            Try it
          </button>
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            className="flex h-10 w-10 items-center justify-center rounded-full text-[#292929] transition-colors hover:bg-[#e9e9e0] lg:hidden"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            data-testid="header-menu-button"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>
      {open && (
        <nav className="border-t border-[#bdbdb4]/60 bg-[#f5f5ee] px-5 py-2 lg:hidden" aria-label="Mobile" data-testid="mobile-menu">
          {NAV_LINKS.map((l) => (
            <NavLink key={l.key} link={l} active={active === l.id} prefix="mobile-nav-link" onNavigate={() => setOpen(false)} className="cd-mobile-link block border-b border-[#e2e2da] py-3 text-base text-[#292929] transition-colors last:border-b-0" />
          ))}
        </nav>
      )}
    </header>
  );
};
